// LiveStreamFloatingAnimation.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DecryptionWrapper from '../container/DecryptionWrapper.js';
import createFloatAnimation from '../style/FloatAnimation.js';
import env from '../resource/env.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';

const ICON_CONFIG = {
  intervalMs: 400, // create new icon interval (ms)
  animationDurationMs: 2000, // base animation duration (ms)
  iconSize: 64, // icon size (px)
  startBottom: 80, // start position from bottom (px)
  maxFloatHeight: 650, // max float height (px)
  maxIcons: 30, // max simultaneous icons count
};

const POSITION_RANGE = {
  min: 10, // min left position
  max: 90, // max left position
};

const DURATION_VARIANCE = 0.5; // animation duration variance (±0.5s)

const generateUniqueIconId = () =>
  `icon-${Date.now()}-${Math.floor(Math.random() * 10000)}`;

const calculateRandomPosition = () =>
  Math.random() * (POSITION_RANGE.max - POSITION_RANGE.min) +
  POSITION_RANGE.min;

const calculateAnimationDuration = baseMs =>
  baseMs / 1000 + (Math.random() - 0.5) * DURATION_VARIANCE * 2;

const cleanupIcon = (iconId, timeoutsMap, setIconsCallback) => {
  const timeoutId = timeoutsMap.get(iconId);
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutsMap.delete(iconId);
  }

  setIconsCallback(prevIcons => prevIcons.filter(icon => icon.id !== iconId));
};

const handleMaxIconsExceeded = (icons, maxCount, timeoutsMap) => {
  const iconsToRemove = icons.slice(0, icons.length - maxCount);

  iconsToRemove.forEach(icon => {
    const timeoutId = timeoutsMap.get(icon.id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutsMap.delete(icon.id);
    }
  });

  return icons.slice(-maxCount);
};

const LiveStreamFloatingAnimation = ({
  shouldTriggerFloatingAnimation = false,
  iconUrl,
}) => {
  const [icons, setIcons] = useState([]);
  const intervalRef = useRef(null);
  const cleanupTimeoutsRef = useRef(new Map()); // store iconId -> timeoutId, use to cleanup icon after animation

  const iconImageConfig = useMemo(() => {
    if (!iconUrl || typeof iconUrl !== 'string') {
      return null;
    }

    const isPublicImage = iconUrl.includes(env.PUBLIC_URL_PREFIX);

    if (isPublicImage) {
      const pngSrcSet = getPublicSrcSet({
        href: iconUrl,
        size: ICON_CONFIG.iconSize,
        format: MediaAssetFormat.PNG,
      });

      return {
        resourceUrl: pngSrcSet.src,
        imageProps: {
          ...pngSrcSet,
          alt: 'floating icon',
        },
      };
    }

    return {
      resourceUrl: iconUrl,
      imageProps: {
        src: iconUrl,
        alt: 'floating icon',
      },
    };
  }, [iconUrl]);

  const createIcon = useCallback(() => {
    if (!iconImageConfig) return;

    const iconId = generateUniqueIconId();
    const duration = calculateAnimationDuration(
      ICON_CONFIG.animationDurationMs
    );

    const newIcon = {
      id: iconId,
      left: calculateRandomPosition(),
      duration,
      iconImageConfig,
    };

    // cleanup icon after animation duration + 1 second
    const timeoutId = setTimeout(
      () => cleanupIcon(iconId, cleanupTimeoutsRef.current, setIcons),
      (duration + 1) * 1000
    );
    cleanupTimeoutsRef.current.set(iconId, timeoutId);

    // update icons array
    setIcons(prevIcons => {
      const updatedIcons = [...prevIcons, newIcon];

      // handle hits max icons count
      if (updatedIcons.length > ICON_CONFIG.maxIcons) {
        return handleMaxIconsExceeded(
          updatedIcons,
          ICON_CONFIG.maxIcons,
          cleanupTimeoutsRef.current
        );
      }

      return updatedIcons;
    });
  }, [iconImageConfig]);

  // Animation main logic
  useEffect(() => {
    const allTimeouts = cleanupTimeoutsRef.current;

    if (!shouldTriggerFloatingAnimation) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      return;
    }

    // === Start animation ===
    createIcon();

    intervalRef.current = setInterval(createIcon, ICON_CONFIG.intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      allTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      allTimeouts.clear();
    };
  }, [shouldTriggerFloatingAnimation, createIcon]);

  if (icons.length === 0) return null;

  return (
    <StyledLiveStreamFloatingAnimation>
      {icons.map(icon => (
        <IconElement
          key={icon.id}
          $left={icon.left}
          $duration={icon.duration}
          $iconSize={ICON_CONFIG.iconSize}
          $startBottom={ICON_CONFIG.startBottom}
          $maxFloatHeight={ICON_CONFIG.maxFloatHeight}
        >
          <DecryptionWrapper resourceUrl={icon.iconImageConfig.resourceUrl}>
            <IconImage {...icon.iconImageConfig.imageProps} />
          </DecryptionWrapper>
        </IconElement>
      ))}
    </StyledLiveStreamFloatingAnimation>
  );
};

LiveStreamFloatingAnimation.propTypes = {
  shouldTriggerFloatingAnimation: PropTypes.bool,
  iconUrl: PropTypes.string.isRequired,
};

const StyledLiveStreamFloatingAnimation = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: visible;
`;

const IconElement = styled.div`
  position: absolute;
  bottom: ${({ $startBottom }) => $startBottom}px;
  left: ${({ $left }) => $left}%;
  width: ${({ $iconSize }) => $iconSize}px;
  height: ${({ $iconSize }) => $iconSize}px;
  animation: ${({ $maxFloatHeight }) => createFloatAnimation($maxFloatHeight)}
    ${({ $duration }) => $duration}s linear forwards;
  user-select: none;
  pointer-events: none;
  transform-origin: center center;
  will-change: transform, opacity;
  backface-visibility: hidden;
  perspective: 1000px;
`;

const IconImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

export default LiveStreamFloatingAnimation;
